"use client"

import type React from "react"
import { useState, useCallback } from "react"
import { Box, Typography, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Tooltip } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import type { UserProfileProps } from "./types"

const UserProfile: React.FC<UserProfileProps> = ({ name, role, initials, menuOptions }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    // Mantener la funcionalidad para abrir el menú
    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }, [])

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    // Ahora ejecutamos onClick solo para la opción de logout
    const handleMenuItemClick = useCallback(
        (onClick: () => void, isLogout: boolean) => {
            if (isLogout) {
                // Si es logout, ejecutamos la función onClick
                onClick()
            }
            handleMenuClose()
        },
        [handleMenuClose],
    )

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "16px", color: "#414141", fontWeight: 500 }}>{name}</Typography>
                <Typography sx={{ fontSize: "14px", color: "#666666" }}>{role}</Typography>
            </Box>
            <Avatar sx={{ bgcolor: "#414141", width: 40, height: 40 }}>{initials}</Avatar>

            <Tooltip title="Open menu">
                <IconButton
                    onClick={handleMenuOpen}
                    sx={{
                        padding: "6px",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                        "&:active": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                    }}
                    aria-label="Open user menu"
                >
                    <MoreVert />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {menuOptions && menuOptions.length > 0 ? (
                    menuOptions.map((option, index) => {
                        // Verificar si la opción es logout por su etiqueta
                        const isLogout =
                            option.label.toLowerCase() === "logout" ||
                            option.label.toLowerCase() === "sign out" ||
                            option.label.toLowerCase() === "cerrar sesión"

                        return (
                            <MenuItem
                                key={index}
                                disabled={!isLogout} // Solo habilitamos la opción de logout
                                onClick={() => handleMenuItemClick(option.onClick, isLogout)}
                            >
                                {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                                <ListItemText>{option.label}</ListItemText>
                            </MenuItem>
                        )
                    })
                ) : (
                    <MenuItem disabled={true}>No options available</MenuItem>
                )}
            </Menu>
        </Box>
    )
}

export default UserProfile

